import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();

    // REMOVED: Manual language detection - now handled by i18n.js
    // The language is automatically loaded from localStorage by i18n configuration



    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const user = await login(email, password);
            if (user.role === 'admin') navigate('/admin');
            else navigate('/client');
        } catch (err) {
            setError(t('invalidCredentials'));
        }
    };

    const isRTL = i18n.language === 'ar';

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-white bg-page-gradient relative overflow-hidden px-4 z-50">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gold/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>




            {/* Back to Home Button (Fixed Top Left) */}
            <a
                href="/"
                className="self-start mb-6 ml-2 md:ml-0 md:absolute md:top-6 md:left-6 md:mb-0 z-20 flex items-center gap-2 px-4 py-2 bg-bg-card/50 hover:bg-bg-card backdrop-blur-md border border-gold/20 rounded-full text-text font-medium transition-all hover:-translate-x-1 group"
            >
                <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {t('backToHome') || 'Back to Home'}
            </a>

            <div className="max-w-md w-full mx-4 relative z-10">
                <div className="bg-bg-card border border-black/5 rounded-2xl shadow-2xl relative">
                    {/* Header with gradient */}
                    <div className="bg-hero-luxury border-b border-gold/10 px-8 py-8 text-center rounded-t-2xl relative">
                        {/* Language Switcher */}
                        <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                            <LanguageSwitcher variant="dark" />
                        </div>
                        <div className="mb-4">
                            <div className="inline-block p-3 bg-bg-soft backdrop-blur-sm rounded-2xl border border-gold/20">
                                <svg className="w-12 h-12 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-2">{t('signInTitle')}</h2>
                        <p className="text-white/80">{t('signInSubtitle')}</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="px-8 py-8 space-y-6">
                        {error && (
                            <div className="bg-danger-50 border border-danger-200 text-danger-700 p-4 rounded-lg text-sm text-center flex items-center gap-2 justify-center animate-shake">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="block text-textMuted text-sm font-bold">{t('emailAddress')} <span className="text-danger-500">*</span></label>
                            <div className="relative">
                                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                    <svg className="w-5 h-5 text-textMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                    </svg>
                                </div>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 bg-bg-soft border border-primary/30 text-text rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-all placeholder-text-subtle`}
                                    placeholder={t('emailPlaceholder')}
                                    required
                                    dir="ltr"
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-textMuted text-sm font-bold">{t('password')} <span className="text-danger-500">*</span></label>
                            <div className="relative">
                                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                    <svg className="w-5 h-5 text-textMuted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 bg-bg-soft border border-primary/30 text-text rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-all placeholder-text-subtle`}
                                    placeholder={t('passwordPlaceholder')}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-gold text-text-invert hover:bg-gold-hover font-bold py-3.5 rounded-lg transition-all duration-300 shadow-md flex items-center justify-center gap-2"
                        >
                            {t('signInButton')}
                            <svg className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="bg-primary/10 px-8 py-4 border-t border-gold/10 text-center rounded-b-2xl">
                        <p className="text-sm text-text-muted font-medium">{t('protectedSystem')}</p>
                    </div>
                </div>

                {/* Footer Links */}
                <div className="mt-8 text-center text-sm">
                    <a
                        href="/privacy"
                        className="text-textMuted hover:text-gold font-medium hover:underline transition-all"
                    >
                        {t('privacyPolicy')}
                    </a>
                </div>
            </div>
        </div>
    );
}
